import React, { useEffect, useMemo, useState } from "react";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import NumbersOutlinedIcon from "@mui/icons-material/NumbersOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import ShortTextOutlinedIcon from "@mui/icons-material/ShortTextOutlined";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { range } from "../../utility";
import NodiantCalendar from "../calendar/NodiantCalendar";
import SurveyFileComponent from "./SurveyFileComponent";

const formatIcons = {
    "Short Text": <ShortTextOutlinedIcon fontSize="small" />,
    "Long Text": <NotesOutlinedIcon fontSize="small" />,
    Number: <NumbersOutlinedIcon fontSize="small" />,
    "True or False": <RadioButtonCheckedOutlinedIcon fontSize="small" />,
    Checkboxes: <CheckBoxOutlinedIcon fontSize="small" />,
    "Multiple Choice": <RadioButtonCheckedOutlinedIcon fontSize="small" />,
    Date: <CalendarMonthOutlinedIcon fontSize="small" />,
    File: <InsertDriveFileOutlinedIcon fontSize="small" />,
};

export default function SurveyQuestion({
    displayNumber = 1,
    onAnswerChange,
    question,
    questionHasBeenAnswered,
    showRequiredText,
    surveyKey,
}) {
    const [textAnswer, setTextAnswer] = useState(
        question.responseTextAnswer || "",
    );
    const [numberAnswer, setNumberAnswer] = useState(
        question.responseNumberAnswer ?? "",
    );
    const [checkboxAnswers, setCheckboxAnswers] = useState(
        question.responseCheckboxAnswers || [],
    );
    const [dateAnswer, setDateAnswer] = useState(
        question.responseDateAnswer || null,
    );

    useEffect(() => {
        setTextAnswer(question.responseTextAnswer || "");
        setNumberAnswer(question.responseNumberAnswer ?? "");
        setCheckboxAnswers(question.responseCheckboxAnswers || []);
        setDateAnswer(question.responseDateAnswer || null);
    }, [
        question.key,
        question.responseCheckboxAnswers,
        question.responseDateAnswer,
        question.responseNumberAnswer,
        question.responseTextAnswer,
    ]);

    const sortedOptions = useMemo(
        () =>
            [...(question.multipleChoiceOptions || [])].sort(
                (first, second) => first.index - second.index,
            ),
        [question.multipleChoiceOptions],
    );
    const numberOptions = useMemo(() => range(151, 0), []);
    const isResource = question.answerFormat === "File";
    const showRequiredError =
        question.answerRequired &&
        showRequiredText &&
        !questionHasBeenAnswered(question);

    function changeAnswer(field, value) {
        onAnswerChange?.(question.key, field, value);
    }

    function handleTextChange(event) {
        const value = event.target.value;
        setTextAnswer(value);
        changeAnswer("responseTextAnswer", value);
    }

    function handleNumberChange(event) {
        const value = event.target.value;
        setNumberAnswer(value);
        changeAnswer("responseNumberAnswer", value);
    }

    function handleCheckboxChange(value) {
        const nextAnswers = checkboxAnswers.includes(value)
            ? checkboxAnswers.filter((answer) => answer !== value)
            : [...checkboxAnswers, value];

        setCheckboxAnswers(nextAnswers);
        changeAnswer("responseCheckboxAnswers", nextAnswers);
    }

    function handleDateChange(value) {
        setDateAnswer(value);
        changeAnswer("responseDateAnswer", value);
    }

    const answerControl = (() => {
        if (question.answerFormat === "Short Text") {
            return (
                <TextField
                    label="Your answer"
                    value={textAnswer}
                    onChange={handleTextChange}
                    fullWidth
                    autoComplete="off"
                    error={showRequiredError}
                />
            );
        }

        if (question.answerFormat === "Long Text") {
            return (
                <TextField
                    label="Your answer"
                    value={textAnswer}
                    onChange={handleTextChange}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={12}
                    error={showRequiredError}
                />
            );
        }

        if (question.answerFormat === "Number") {
            const labelId = `number-answer-label-${question.key}`;
            return (
                <FormControl
                    fullWidth
                    error={showRequiredError}
                    sx={{ maxWidth: 260 }}
                >
                    <InputLabel id={labelId}>Your answer</InputLabel>
                    <Select
                        labelId={labelId}
                        value={numberAnswer}
                        label="Your answer"
                        onChange={handleNumberChange}
                    >
                        <MenuItem value="">
                            <em>Select a number</em>
                        </MenuItem>
                        {numberOptions.map((number) => (
                            <MenuItem key={number} value={number}>
                                {number}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        if (question.answerFormat === "True or False") {
            return (
                <RadioGroup
                    aria-label="Your answer"
                    value={textAnswer}
                    onChange={handleTextChange}
                    sx={{ gap: 0.75 }}
                >
                    {["True", "False"].map((option) => (
                        <Box key={option} sx={choiceSx}>
                            <FormControlLabel
                                value={option}
                                control={<Radio />}
                                label={option}
                                sx={{ width: "100%", m: 0 }}
                            />
                        </Box>
                    ))}
                </RadioGroup>
            );
        }

        if (question.answerFormat === "Checkboxes") {
            return (
                <FormGroup sx={{ gap: 0.75 }}>
                    {sortedOptions.map((option) => (
                        <Box key={option.key} sx={choiceSx}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checkboxAnswers.includes(
                                            option.text,
                                        )}
                                        onChange={() =>
                                            handleCheckboxChange(option.text)
                                        }
                                    />
                                }
                                label={option.text}
                                sx={{ width: "100%", m: 0 }}
                            />
                        </Box>
                    ))}
                </FormGroup>
            );
        }

        if (question.answerFormat === "Multiple Choice") {
            return (
                <RadioGroup
                    aria-label="Your answer"
                    value={textAnswer}
                    onChange={handleTextChange}
                    sx={{ gap: 0.75 }}
                >
                    {sortedOptions.map((option) => (
                        <Box key={option.key} sx={choiceSx}>
                            <FormControlLabel
                                value={option.text}
                                control={<Radio />}
                                label={option.text}
                                sx={{ width: "100%", m: 0 }}
                            />
                        </Box>
                    ))}
                </RadioGroup>
            );
        }

        if (question.answerFormat === "Date") {
            return (
                <Box
                    sx={{
                        maxWidth: 560,
                        border: "1px solid",
                        borderColor: showRequiredError
                            ? "error.main"
                            : "divider",
                        borderRadius: 2.5,
                        p: { xs: 1, sm: 1.5 },
                    }}
                >
                    <NodiantCalendar
                        selectedDate={dateAnswer}
                        onDateChange={handleDateChange}
                    />
                </Box>
            );
        }

        if (isResource) {
            return (
                <SurveyFileComponent
                    surveyKey={surveyKey}
                    isEditing={false}
                    question={question}
                />
            );
        }

        return (
            <Typography variant="body2" color="text.secondary">
                This question type is not available.
            </Typography>
        );
    })();

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                overflow: "hidden",
                borderColor: showRequiredError
                    ? "error.main"
                    : "divider",
                boxShadow: showRequiredError
                    ? "0 0 0 2px rgba(211, 47, 47, 0.08)"
                    : "0 5px 18px rgba(20, 35, 25, 0.045)",
            }}
        >
            <Box
                sx={{
                    minHeight: 64,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    backgroundColor: "background.subtle",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 1.75,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    {formatIcons[question.answerFormat] || (
                        <HelpOutlineIcon fontSize="small" />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {isResource
                            ? `Resource ${displayNumber}`
                            : `Question ${displayNumber}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {isResource
                            ? "Linked resource"
                            : question.answerFormat}
                    </Typography>
                </Box>
                {!isResource && (
                    <Chip
                        size="small"
                        label={question.answerRequired ? "Required" : "Optional"}
                        sx={{
                            backgroundColor: question.answerRequired
                                ? "rgba(0, 149, 61, 0.09)"
                                : "action.hover",
                            color: question.answerRequired
                                ? "primary.dark"
                                : "text.secondary",
                        }}
                    />
                )}
            </Box>

            <CardContent
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    "&:last-child": { pb: { xs: 2, sm: 2.5 } },
                }}
            >
                {!isResource && (
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                            mb: 2,
                            fontFamily: "QuatroRegular",
                            fontSize: { xs: 18, sm: 20 },
                            lineHeight: 1.35,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {question.questionText || "Untitled question"}
                    </Typography>
                )}

                {answerControl}

                {showRequiredError && (
                    <Typography
                        role="alert"
                        variant="body2"
                        color="error"
                        sx={{ mt: 1.25, fontWeight: 700 }}
                    >
                        Please answer this required question.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

const choiceSx = {
    px: 1.25,
    py: 0.5,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    transition: "background-color 120ms ease, border-color 120ms ease",
    "&:hover": {
        borderColor: "rgba(0, 149, 61, 0.32)",
        backgroundColor: "rgba(0, 149, 61, 0.035)",
    },
};
